import { useMemo, useState } from "react";
import { FormatterMode, resolveFormatterConfig } from "@textformatter/formatter";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Textarea } from "../ui/textarea";
import FormatterControls from "./FormatterControls";
import FormatterOutputPanel from "./FormatterOutputPanel";
import { createModeProfiles, createTransformProfiles, useFormattedHtml } from "./formatting";
import type {
  FormatterMode as FormatterModeValue,
  FormatterModeProfiles,
  FormatterOptions,
  FormatterTransforms,
} from "./types";

type FormatterExampleCardProps = {
  title: string;
  titleHref?: string;
  showTitle?: boolean;
  initialInput: string;
  onInputChange?: (value: string) => void;
  defaultMode?: FormatterModeValue;
  defaultOptions?: Partial<FormatterOptions>;
  defaultTransforms?: Partial<FormatterTransforms>;
  defaultOptionsByMode?: Partial<Record<FormatterModeValue, Partial<FormatterOptions>>>;
};

export default function FormatterExampleCard({
  title,
  titleHref,
  showTitle = true,
  initialInput,
  onInputChange,
  defaultMode = FormatterMode.Web,
  defaultOptions,
  defaultTransforms,
  defaultOptionsByMode,
}: FormatterExampleCardProps) {
  const [input, setInput] = useState(initialInput);
  const [mode, setMode] = useState<FormatterModeValue>(defaultMode);
  const [modeProfiles, setModeProfiles] = useState<FormatterModeProfiles>(() => {
    const profiles = createModeProfiles(defaultOptionsByMode);
    if (defaultOptions) {
      profiles[defaultMode] = resolveFormatterConfig({
        mode: defaultMode,
        options: { ...profiles[defaultMode], ...defaultOptions },
        fallbackMode: FormatterMode.Text,
      });
    }
    return profiles;
  });
  const [transformProfiles, setTransformProfiles] = useState<Record<FormatterModeValue, FormatterTransforms>>(
    () =>
      createTransformProfiles(
        defaultTransforms
          ? {
              [defaultMode]: defaultTransforms,
            }
          : undefined,
      ),
  );

  const activeOptions = useMemo(() => modeProfiles[mode], [modeProfiles, mode]);
  const activeTransforms = useMemo(() => transformProfiles[mode], [transformProfiles, mode]);
  const { output, format } = useFormattedHtml(input, mode, activeOptions, activeTransforms);

  function updateActiveOptions(next: FormatterOptions) {
    setModeProfiles((prev) => ({ ...prev, [mode]: next }));
  }

  function updateActiveTransforms(next: FormatterTransforms) {
    setTransformProfiles((prev) => ({ ...prev, [mode]: next }));
  }

  return (
    <section className="space-y-3">
      {showTitle ? (
        <h2 className="text-sm uppercase tracking-[0.2em] text-slate-500">
          {titleHref ? (
            <a href={titleHref} className="hover:text-slate-700 hover:underline">
              {title}
            </a>
          ) : (
            title
          )}
        </h2>
      ) : null}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Input</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={input}
              onChange={(e) => {
                const next = e.target.value;
                setInput(next);
                onInputChange?.(next);
              }}
              placeholder="Example input"
            />
            <FormatterControls
              mode={mode}
              onModeChange={setMode}
              options={activeOptions}
              onChange={updateActiveOptions}
              transforms={activeTransforms}
              onTransformsChange={updateActiveTransforms}
              showOptionDescriptions={false}
              showFieldLabels
            />
          </CardContent>
        </Card>
        <FormatterOutputPanel output={output} format={format} title="Output" />
      </div>
    </section>
  );
}
